import { useContext, ChangeEvent, ReactNode } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AuturaTextField from "components/inputs/AuturaTextField";
import { useTranslation } from "react-i18next";
import AuturaDateTimePicker from "components/inputs/AuturaDateTimePicker";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import AuturaStateField from "components/inputs/AuturaStateField";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
dayjs.extend(customParseFormat);

interface ImportFieldInputProps<F extends string> {
  field: F;
  value: string;
  setValue: (value: string) => void;
}

export const ImportFieldInput = <F extends string>({
  field,
  value,
  setValue,
}: ImportFieldInputProps<F>): ReactNode => {
  const { t } = useTranslation();
  const { fieldMetadata } = useContext(ImportWizardContext);

  if (fieldMetadata[field]?.type === "enum") {
    return (
      <AuturaDropdown
        label={t(field as string, { ns: "entities" })}
        name={field}
        value={value}
        setValue={(e: ChangeEvent<HTMLInputElement>) =>
          setValue(e.target.value.replace(/ /g, "_"))
        }
        options={
          fieldMetadata[field]?.enumValues?.map((value) =>
            value.replace(/_/g, " "),
          ) || []
        }
      />
    );
  }

  if (fieldMetadata[field]?.type === "date") {
    return (
      <AuturaDateTimePicker
        label={`${t("New")} ${t(field as string, { ns: "entities" })}`}
        dateTime={new Date(value)}
        setDateTime={(dateTime: Date | null) => {
          if (dateTime) {
            setValue(dateTime.toISOString());
          } else {
            setValue("");
          }
        }}
        readonly={false}
      />
    );
  }

  if (field.includes("state")) {
    // states
    return (
      <AuturaStateField
        stateCode={value}
        setStateCode={(stateCode: string) => setValue(stateCode)}
        readonly={false}
      />
    );
  }

  return (
    <AuturaTextField
      name="newValue"
      label={t("validationWidget.new_value", { ns: "importWizard" })}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      fullWidth
    />
  );
};
